import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { hostname } from "../utils";

// Import CRM logos
import hubspotLogo from "../img/hubspot.svg";
import gohighlevelLogo from "../img/gohighlevel.svg";
import salesforceLogo from "../img/salesforce.png";

interface CrmIntegration {
  id: string;
  type: string;
  name: string;
  instanceName?: string;
  status?: string;
}

interface CrmIntegrationSelectProps {
  value: string;
  onChange: (integrationId: string) => void;
  getToken: () => Promise<string>;
  companyId: string;
  label?: string;
  helperText?: string;
  showTooltip?: boolean;
  tooltipText?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  onIntegrationsLoaded?: (integrations: CrmIntegration[]) => void;
  onValidationChange?: (isValid: boolean) => void;
}

// Map integration type to display name
const getCrmTypeName = (type: string): string => {
  switch (type) {
    case "hubspot":
      return "HubSpot";
    case "gohighlevel":
      return "GoHighLevel";
    case "salesforce":
      return "Salesforce";
    default:
      return type;
  }
};

// Map integration type to logo
const getCrmLogo = (type: string): string => {
  switch (type) {
    case "hubspot":
      return hubspotLogo;
    case "gohighlevel":
      return gohighlevelLogo;
    case "salesforce":
      return salesforceLogo;
    default:
      return "";
  }
};

// Get display name for integration
const getIntegrationDisplayName = (integration: CrmIntegration): string => {
  const typeName = getCrmTypeName(integration.type);
  const instanceName = integration.instanceName || integration.name;
  return `${typeName} - ${instanceName}`;
};

export default function CrmIntegrationSelect({
  value,
  onChange,
  getToken,
  companyId,
  label = "CRM Integration",
  helperText = "Contacts will be created or updated in this CRM",
  showTooltip = false,
  tooltipText = "Select which CRM to sync contacts to. The action will create or update contacts with the row's lead data.",
  disabled = false,
  required = false,
  fullWidth = true,
  size = "medium",
  onIntegrationsLoaded,
  onValidationChange,
}: CrmIntegrationSelectProps) {
  const [crmIntegrations, setCrmIntegrations] = useState<CrmIntegration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch CRM integrations
  useEffect(() => {
    if (!getToken || !companyId) return;

    const fetchIntegrations = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/crm/integrations`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });

        const integrations = response.data.integrations || [];
        setCrmIntegrations(integrations);

        // Notify parent of loaded integrations
        if (onIntegrationsLoaded) {
          onIntegrationsLoaded(integrations);
        }

        // Auto-select if there's only one integration and no value selected
        if (integrations.length === 1 && !value) {
          onChange(integrations[0].id);
        }
      } catch (err) {
        console.error("Error fetching CRM integrations:", err);
        setError("Failed to load CRM integrations");
        setCrmIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [getToken, companyId]);

  // Validate and notify parent
  useEffect(() => {
    if (onValidationChange) {
      const isValid = !required || Boolean(value && value.trim().length > 0);
      onValidationChange(isValid);
    }
  }, [value, required, onValidationChange]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  // Render the selected value with logo
  const renderValue = (selectedId: string) => {
    if (!selectedId) {
      return <em>Select a CRM integration</em>;
    }
    const integration = crmIntegrations.find((i) => i.id === selectedId);
    if (!integration) {
      return <em>Select a CRM integration</em>;
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Avatar
          src={getCrmLogo(integration.type)}
          sx={{ width: 24, height: 24 }}
          variant="rounded"
        />
        <span>{getIntegrationDisplayName(integration)}</span>
      </Box>
    );
  };

  return (
    <Box>
      {showTooltip && (
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          {label}
          <Tooltip title={tooltipText}>
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && crmIntegrations.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No CRM integrations found. Please set up a CRM integration (HubSpot,
          GoHighLevel, or Salesforce) in the Integrations settings first.
        </Alert>
      )}

      <FormControl
        fullWidth={fullWidth}
        disabled={disabled || loading || crmIntegrations.length === 0}
        size={size}
        required={required}
      >
        {!showTooltip && <InputLabel>{label}</InputLabel>}
        <Select
          value={value || ""}
          onChange={handleChange}
          label={!showTooltip ? label : undefined}
          renderValue={renderValue}
          startAdornment={
            loading ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : undefined
          }
        >
          {loading && (
            <MenuItem value="" disabled>
              Loading integrations...
            </MenuItem>
          )}
          {!loading && crmIntegrations.length === 0 && (
            <MenuItem value="" disabled>
              No CRM integrations available
            </MenuItem>
          )}
          {crmIntegrations.map((integration) => (
            <MenuItem key={integration.id} value={integration.id}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar
                  src={getCrmLogo(integration.type)}
                  sx={{ width: 28, height: 28 }}
                  variant="rounded"
                />
              </ListItemAvatar>
              <ListItemText
                primary={getIntegrationDisplayName(integration)}
                secondary={integration.status}
              />
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
}

// Export helper functions for use in other components if needed
export { getCrmTypeName, getCrmLogo, getIntegrationDisplayName };
export type { CrmIntegration };
